import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { useSplash } from '../../context/SplashContext';
import { MdShowChart, MdPieChart, MdInsights, MdCandlestickChart } from 'react-icons/md';
import SignalField from '../../components/SignalField';

// Landing — three "portals" in a row (Trading Engine · Intelligence Platform ·
// HF Analytics), each a tactile gateway (cursor spotlight + 3D tilt + a living
// canvas texture). Below them, an ambient "signals converging into a core" field.
// No gated data on the landing, so it renders for every user.

const BOLT = (
  <svg viewBox="0 0 24 24" fill="currentColor" width="26" height="26"><path d="M13 2 3 14h7l-1 8 10-12h-7l1-8z" /></svg>
);
const NODE = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" width="26" height="26">
    <circle cx="12" cy="12" r="2.4" fill="currentColor" stroke="none" />
    <circle cx="4.5" cy="6" r="1.5" /><circle cx="20" cy="7.5" r="1.5" /><circle cx="6" cy="19" r="1.5" /><circle cx="18.5" cy="18" r="1.5" />
    <path d="M10 11 5.6 6.9M14 11l4.8-2.9M10.6 13.5 6.9 18M13.6 13.4l4-4" />
  </svg>
);
const CHART = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" width="26" height="26">
    <path d="M4 19V5m0 14h16M4 15l4-4 3 2 6-7" /><circle cx="17" cy="6" r="1.4" fill="currentColor" stroke="none" />
  </svg>
);
// ResearchOS emblem — a radar/scope (intelligence-gathering): concentric rings + crosshair.
const SCOPE = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" width="26" height="26">
    <circle cx="12" cy="12" r="8.5" /><circle cx="12" cy="12" r="4" />
    <path d="M12 3.5v3M12 17.5v3M3.5 12h3M17.5 12h3" />
    <circle cx="12" cy="12" r="1.2" fill="currentColor" stroke="none" />
  </svg>
);

// ResearchOS is now its OWN top-level portal (below), no longer nested here.
const INTEL_APPS = [
  { name: 'Technical Signals', path: '/signals', icon: MdInsights, perm: 'signals' },
  { name: 'Saudi Market', path: '/saudi', icon: MdCandlestickChart, perm: 'saudi' },
  { name: 'GCC Flows', path: '/gcc', icon: MdShowChart, perm: 'saudi' },
  { name: 'Asset Allocation', path: '/saa', icon: MdPieChart, perm: 'saa' },
];

// living texture inside each portal — trading: ticker mesh; intel: constellation;
// hf: drifting fund curves. redraws on accent (theme) change.
function PortalCanvas({ kind, accent }) {
  const ref = useRef(null);
  useEffect(() => {
    const cv = ref.current; if (!cv) return undefined;
    const ctx = cv.getContext('2d');
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let w = 0, h = 0, raf = 0, t = 0, pts = null, tape = null, curves = null;
    const parent = cv.parentElement;
    const size = () => {
      const r = parent.getBoundingClientRect(); const dpr = Math.min(2, window.devicePixelRatio || 1);
      cv.width = r.width * dpr; cv.height = r.height * dpr; ctx.setTransform(dpr, 0, 0, dpr, 0, 0); w = r.width; h = r.height;
      if (kind === 'intel' || kind === 'research') pts = Array.from({ length: 20 }, () => ({ x: Math.random() * w, y: Math.random() * h, vx: (Math.random() - 0.5) * 0.16, vy: (Math.random() - 0.5) * 0.16 }));
      else if (kind === 'trading') tape = Array.from({ length: 7 }, (_, i) => ({ y: 12 + i * ((h - 24) / 6), off: Math.random() * w, sp: 0.25 + Math.random() * 0.5 }));
      else curves = Array.from({ length: 4 }, (_, i) => ({ base: h * (0.35 + i * 0.13), amp: 8 + i * 3, freq: 0.012 + i * 0.004, ph: Math.random() * 6, sp: 0.006 + i * 0.002 }));
    };
    const draw = () => {
      t += 1; ctx.clearRect(0, 0, w, h);
      if (kind === 'trading') {
        ctx.strokeStyle = accent; ctx.globalAlpha = 0.06; ctx.lineWidth = 1;
        tape.forEach((l) => { ctx.beginPath(); ctx.moveTo(0, l.y); ctx.lineTo(w, l.y); ctx.stroke(); });
        ctx.fillStyle = accent;
        tape.forEach((l) => { for (let k = 0; k < 4; k++) { const x = ((l.off + t * l.sp + k * (w / 4)) % (w + 40)) - 20; ctx.globalAlpha = 0.1 + 0.16 * (1 - Math.abs(x - w * 0.7) / w); ctx.fillRect(x, l.y - 1, 9, 2); } });
      } else if (kind === 'intel' || kind === 'research') {
        const P = pts; ctx.fillStyle = accent;
        P.forEach((a) => { a.x += a.vx; a.y += a.vy; if (a.x < 0 || a.x > w) a.vx *= -1; if (a.y < 0 || a.y > h) a.vy *= -1; });
        for (let i = 0; i < P.length; i++) for (let j = i + 1; j < P.length; j++) { const dx = P[i].x - P[j].x, dy = P[i].y - P[j].y, d = Math.hypot(dx, dy); if (d < 88) { ctx.globalAlpha = 0.14 * (1 - d / 88); ctx.strokeStyle = accent; ctx.lineWidth = 1; ctx.beginPath(); ctx.moveTo(P[i].x, P[i].y); ctx.lineTo(P[j].x, P[j].y); ctx.stroke(); } }
        ctx.globalAlpha = 0.5; P.forEach((a) => { ctx.beginPath(); ctx.arc(a.x, a.y, 1.3, 0, 7); ctx.fill(); });
      } else {
        ctx.strokeStyle = accent; ctx.lineWidth = 1.4;
        curves.forEach((c) => { ctx.globalAlpha = 0.13; ctx.beginPath(); for (let x = 0; x <= w; x += 6) { const y = c.base + c.amp * Math.sin(x * c.freq + c.ph + t * c.sp); x === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y); } ctx.stroke(); });
      }
      ctx.globalAlpha = 1; raf = window.requestAnimationFrame(draw);
    };
    size(); const ro = new ResizeObserver(size); ro.observe(parent);
    if (reduce) draw(); else raf = window.requestAnimationFrame(draw);
    return () => { window.cancelAnimationFrame(raf); ro.disconnect(); };
  }, [kind, accent]);
  return <canvas ref={ref} className="absolute inset-0 w-full h-full opacity-50 group-hover:opacity-95 transition-opacity duration-500" style={{ zIndex: -2 }} aria-hidden="true" />;
}

function Portal({ kind, title, desc, emblem, onEnter, accent, extra = '' }) {
  const ref = useRef(null);
  const onMove = (e) => {
    const el = ref.current; if (!el) return; const r = el.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width, y = (e.clientY - r.top) / r.height;
    el.style.setProperty('--ry', `${((x - 0.5) * 6).toFixed(2)}deg`);
    el.style.setProperty('--rx', `${((0.5 - y) * 5).toFixed(2)}deg`);
    el.style.setProperty('--mx', `${(x * 100).toFixed(1)}%`); el.style.setProperty('--my', `${(y * 100).toFixed(1)}%`);
  };
  const onLeave = () => { const el = ref.current; if (!el) return; el.style.setProperty('--rx', '0deg'); el.style.setProperty('--ry', '0deg'); };
  return (
    <button ref={ref} onClick={onEnter} onMouseMove={onMove} onMouseLeave={onLeave}
      className={`portal group relative text-left rounded-2xl border border-gray-200 dark:border-gray-800 overflow-hidden p-5 min-h-[168px] md:min-h-[204px] flex flex-col justify-between isolate bg-gradient-to-br from-white to-gray-50 dark:from-[#15171d] dark:to-[#111318] hover:border-brand-gold/50 transition-all duration-300 ${extra}`}
      style={{ transform: 'perspective(1200px) rotateX(var(--rx,0)) rotateY(var(--ry,0))', boxShadow: '0 22px 60px -44px rgba(0,0,0,.5)' }}>
      <PortalCanvas kind={kind} accent={accent} />
      <span className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{ zIndex: -1, background: 'radial-gradient(300px 300px at var(--mx,50%) var(--my,40%), rgb(var(--brand-gold-rgb)/0.2), transparent 60%)' }} />
      <span className="pointer-events-none absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-brand-gold/60 to-transparent opacity-50 group-hover:opacity-100 transition-opacity" />
      <div className="flex items-center justify-between">
        <span className="w-12 h-12 rounded-xl grid place-items-center text-brand-gold bg-brand-gold/10 border border-brand-gold/40">{emblem}</span>
        <span className="font-mono text-[9px] tracking-[0.15em] uppercase text-gray-400 flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-green-400" style={{ boxShadow: '0 0 7px #4ade80' }} /> Online</span>
      </div>
      <div>
        <h2 className="font-serif text-[clamp(15px,1.5vw,19px)] text-gray-900 dark:text-gray-100 tracking-wide">{title}</h2>
      </div>
    </button>
  );
}

// The ambient "signals converging into a core" field lives in components/SignalField.js
// (shared with the full-page SplashScreen — single source of truth).

const DashboardPage = () => {
  const { user } = useAuth();
  const perms = user?.profile?.app_permissions || {};
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const { playSplash } = useSplash();
  const accent = isDark ? '#c9a96e' : '#3b82f6';
  const [now, setNow] = useState(new Date());
  const [view, setView] = useState('portals');

  useEffect(() => { const t = setInterval(() => setNow(new Date()), 60000); return () => clearInterval(t); }, []);

  // A delegated operator (e.g. Joji) reaches trading through an AccountDelegate, not
  // access_trading — so the perms.trading gate alone would hide the tile. Show it for them too.
  const isOperator = !!(user?.trading_delegate?.is_delegate) && !user?.is_admin;
  const canTrade = perms.trading !== false || isOperator;
  const canHf = perms.hf !== false;
  const intelApps = useMemo(() => INTEL_APPS.filter((a) => !a.perm || perms[a.perm] !== false), [perms]);

  // Tile click → play the boot splash as the loading transition, then navigate.
  // (The Intelligence tile just expands its app grid inline — no splash needed.)
  const ignite = useCallback((dest, opts) => {
    if (dest) playSplash(() => navigate(dest), opts?.durationMs || 3000, opts);
    else setView('intel');
  }, [navigate, playSplash]);

  const date = now.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  const time = now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });

  return (
    <div className="relative">
      <div className="flex items-center justify-end gap-3 mb-6">
        <span className="font-mono text-[9px] tracking-[0.2em] uppercase text-red-500 dark:text-red-400 border border-red-300 dark:border-red-800 rounded px-2 py-1">Confidential</span>
        <span className="text-xs text-gray-400 dark:text-gray-500 font-mono">{date} · {time}</span>
      </div>

      {view === 'portals' && (
        <>
          <div className="max-w-5xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" style={{ perspective: '1200px' }}>
            {canTrade && (
              <Portal kind="trading" title="Trading Engine" emblem={BOLT} accent={accent}
                desc="Portfolio, strategies, execution & data." onEnter={() => ignite('/trading/portfolio')} />
            )}
            <Portal kind="research" title="Research OS" emblem={SCOPE} accent={accent}
              desc="Company scorecards & social-signal intelligence."
              onEnter={() => ignite('/researchos', { spinner: true, durationMs: 6000, tagline: 'Where human expertise meets agentic intelligence' })} />
            {intelApps.length > 0 && (
              <Portal kind="intel" title="Intelligence Platform" emblem={NODE} accent={accent}
                desc="Signals, flows & allocation research." onEnter={() => ignite(null)} />
            )}
            {canHf && (
              <Portal kind="hf" title="HF Analytics" emblem={CHART} accent={accent}
                desc="Hedge-fund performance & risk." onEnter={() => ignite('/hf')} />
            )}
          </div>

          {/* ambient signal field in the space below */}
          <div className="relative mt-6 rounded-2xl overflow-hidden border border-gray-100 dark:border-gray-900" style={{ height: 'clamp(230px, 42vh, 440px)' }}>
            <SignalField accent={accent} />
            <div className="absolute inset-0 flex items-end justify-center pb-6 pointer-events-none">
              <span className="font-mono text-[9.5px] tracking-[0.34em] uppercase text-gray-400/70 dark:text-gray-600/70">Signals in · intelligence out</span>
            </div>
          </div>
        </>
      )}

      {view === 'intel' && (
        <>
          <div className="flex items-center gap-2 mb-5 text-xs">
            <button onClick={() => setView('portals')} className="text-gray-500 dark:text-gray-400 hover:text-brand-gold transition-colors flex items-center gap-1">‹ Platforms</button>
            <span className="text-gray-300 dark:text-gray-600">/</span>
            <span className="text-gray-700 dark:text-gray-200 font-medium">Intelligence Platform</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {intelApps.map((a) => {
              const Icon = a.icon;
              return (
                <Link key={a.path} to={a.path} className="group bg-white dark:bg-[#15171d] border border-gray-200 dark:border-gray-800 rounded-xl p-4 flex flex-col gap-3 hover:border-brand-gold hover:shadow-sm transition-all">
                  <span className="w-10 h-10 rounded-lg grid place-items-center bg-brand-gold/10 text-brand-gold group-hover:bg-brand-gold group-hover:text-white transition-colors"><Icon size={22} /></span>
                  <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">{a.name}</span>
                </Link>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default DashboardPage;
